import { useCallback, useEffect, useState } from "react";
import {
  type ObjectBreakdown,
  type StorageUserDetail,
  fetchStorageDetail,
  recalculateStorage,
  runCleanup,
} from "../../api/admin-content";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  LoadingIndicator,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";
import { StorageSyncCursorTable } from "./StorageSyncCursorTable";
import { StorageTraceTable } from "./StorageTraceTable";
import { formatBytes } from "./storage-detail-formatters";

type ConfirmAction = "recalculate" | "cleanup";
type DetailTab = "trace" | "breakdown" | "sync";

const DETAIL_TABS: ReadonlyArray<DetailTab> = ["trace", "breakdown", "sync"];

export function StorageDetailPanel({
  userId,
  onActionDone,
}: {
  userId: string;
  onActionDone: () => void;
}): React.JSX.Element {
  const { detail, loading } = useStorageDetail(userId);
  const [activeTab, setActiveTab] = useState<DetailTab>("trace");
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const handleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      try {
        await runConfirmedAction(confirm, userId, reason, setMsg);
        onActionDone();
      } catch (e) {
        setMsg(e instanceof Error ? e.message : t("common.action_failed"));
      } finally {
        setConfirm(null);
      }
    },
    [confirm, userId, onActionDone],
  );

  if (loading) return <LoadingIndicator />;
  if (!detail)
    return <p style={ts.emptyText}>{t("storage_detail.failed_to_load")}</p>;

  return (
    <div style={ts.detailPanel}>
      <DetailTabs active={activeTab} onChange={setActiveTab} />
      <ActiveTabPanel activeTab={activeTab} detail={detail} />
      {msg && <p style={ts.actionMsg}>{msg}</p>}
      <StorageActions
        confirm={confirm}
        onAction={setConfirm}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

function useStorageDetail(userId: string): {
  readonly detail: StorageUserDetail | null;
  readonly loading: boolean;
} {
  const [detail, setDetail] = useState<StorageUserDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchStorageDetail(userId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [userId]);

  return { detail, loading };
}

async function runConfirmedAction(
  action: ConfirmAction | null,
  userId: string,
  reason: string,
  setMsg: (value: string) => void,
): Promise<void> {
  if (action === "recalculate") {
    const r = await recalculateStorage(userId, reason);
    setMsg(
      t("storage_detail.recalculated_message", {
        previous: formatBytes(r.previous_bytes),
        current: formatBytes(r.recalculated_bytes),
      }),
    );
  } else if (action === "cleanup") {
    const r = await runCleanup(userId, reason);
    setMsg(t("storage_detail.cleanup_message", { count: r.marked_count }));
  }
}

function DetailTabs({
  active,
  onChange,
}: {
  active: DetailTab;
  onChange: (tab: DetailTab) => void;
}): React.JSX.Element {
  return (
    <div style={styles.tabBar}>
      {DETAIL_TABS.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(tab)}
          style={{
            ...ts.filterBtn,
            ...(active === tab ? ts.filterBtnActive : {}),
          }}
        >
          {t(`storage_detail.tab_${tab}`)}
        </button>
      ))}
    </div>
  );
}

function ActiveTabPanel({
  activeTab,
  detail,
}: {
  activeTab: DetailTab;
  detail: StorageUserDetail;
}): React.JSX.Element {
  if (activeTab === "trace") {
    return <StorageTraceTable changes={detail.recent_changes} />;
  }
  if (activeTab === "sync") {
    return <StorageSyncCursorTable cursors={detail.sync_cursors} />;
  }
  return <BreakdownList items={detail.breakdown} />;
}

function BreakdownList({
  items,
}: {
  items: ReadonlyArray<ObjectBreakdown>;
}): React.JSX.Element {
  if (items.length === 0) {
    return <p style={ts.emptyText}>{t("storage_detail.no_breakdown")}</p>;
  }
  return (
    <div style={ts.detailSection}>
      <h4 style={ts.detailTitle}>{t("storage_detail.breakdown")}</h4>
      {items.map((item) => (
        <div key={item.object_type} style={ts.detailRow}>
          <span>{item.object_type}</span>
          <span>
            {t("storage_detail.object_count_bytes", {
              count: item.count,
              bytes: formatBytes(item.size_bytes),
            })}
          </span>
        </div>
      ))}
    </div>
  );
}

function ActionButtons({
  onAction,
}: {
  onAction: (action: ConfirmAction) => void;
}): React.JSX.Element {
  return (
    <div style={ts.detailActions}>
      <button
        type="button"
        style={ts.actionBtn}
        onClick={() => onAction("recalculate")}
      >
        {t("storage_detail.recalculate")}
      </button>
      <button
        type="button"
        style={{ ...ts.actionBtn, ...ts.dangerBtn }}
        onClick={() => onAction("cleanup")}
      >
        {t("storage_detail.run_cleanup")}
      </button>
    </div>
  );
}

function StorageActions({
  confirm,
  onAction,
  onConfirm,
}: {
  confirm: ConfirmAction | null;
  onAction: (action: ConfirmAction | null) => void;
  onConfirm: (reason: string) => Promise<void>;
}): React.JSX.Element {
  return (
    <>
      <ActionButtons onAction={onAction} />
      <ConfirmDialog
        open={confirm === "recalculate"}
        title={t("storage_detail.recalculate_title")}
        description={t("storage_detail.recalculate_desc")}
        reasonRequired
        onConfirm={onConfirm}
        onCancel={() => onAction(null)}
      />
      <ConfirmDialog
        open={confirm === "cleanup"}
        title={t("storage_detail.cleanup_title")}
        description={t("storage_detail.cleanup_desc")}
        reasonRequired
        onConfirm={onConfirm}
        onCancel={() => onAction(null)}
      />
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  tabBar: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 },
};
